// Manual control of the attention set: who may change it, the override
// changes behind each action, their optimistic effect, and the people
// offered when adding someone.
import type { AttentionMember, Reason } from '../engine/types';
import type { PullRequestData, ReviewThread, TimelineItem } from '../api/queries';
import type { ServerOverride } from '../api/server';
import { isBot, reviewerLogin } from './timeline';

/** One override write: `add`/`remove` sets it, null clears it. */
export interface AttentionChange {
  login: string;
  state: 'add' | 'remove' | null;
}

/** Repository permissions (GraphQL `viewerPermission`) that may change anyone's attention. */
const TRIAGE_OR_MORE = new Set(['TRIAGE', 'WRITE', 'MAINTAIN', 'ADMIN']);

export interface AttentionWriter {
  viewer: string;
  author: string;
  /** `viewerPermission` from the PR query; null when unknown (writes are tried). */
  permission: string | null | undefined;
}

/** A login's canonical form for keys: ASCII-lowercased, as the server stores override logins. */
export const loginKey = (login: string): string => login.replace(/[A-Z]/g, (c) => c.toLowerCase());

/**
 * Whether two logins name the same account: ASCII case-insensitive, as
 * GitHub logins are. An empty login matches nothing. The server stores
 * override logins lowercased, so compare logins with this, never `===`.
 */
export function sameLogin(a: string, b: string): boolean {
  return !!a && loginKey(a) === loginKey(b);
}

/** Whether `login` is in `members` (by sameLogin). */
export const hasMember = (members: readonly AttentionMember[], login: string): boolean =>
  members.some((m) => sameLogin(m.login, login));

/**
 * Whether the viewer may write an override for `login`. Mirrors the
 * server's rule: one's own login always; anyone's with triage access or
 * more, or as the PR's author. An unknown permission allows the attempt
 * (the server answers 403 and the error is shown).
 */
export function canSetAttention(login: string, w: AttentionWriter): boolean {
  if (sameLogin(login, w.viewer)) return true;
  if (sameLogin(w.author, w.viewer)) return true;
  if (!w.permission) return true;
  return TRIAGE_OR_MORE.has(w.permission.toUpperCase());
}

/** Whether the viewer may change other people's attention (add, set turn, remove others). */
export function canSetOthers(w: AttentionWriter): boolean {
  return canSetAttention('\u0000other', w);
}

/** "Remove from attention". */
export function removeChanges(login: string): AttentionChange[] {
  return [{ login, state: 'remove' }];
}

/** "+ Add". */
export function addChanges(login: string): AttentionChange[] {
  return [{ login, state: 'add' }];
}

/**
 * "It's only <login>'s turn" / "Set turn…": add `login` and remove
 * everyone else currently in the set.
 */
export function onlyChanges(login: string, members: AttentionMember[]): AttentionChange[] {
  return [
    { login, state: 'add' },
    ...members.filter((m) => !sameLogin(m.login, login)).map((m) => ({ login: m.login, state: 'remove' as const })),
  ];
}

/**
 * The overrides after `changes` (one per login; the server keeps the
 * latest), each new one carrying `written`: its basis, writer and time.
 */
export function applyToOverrides(
  overrides: ServerOverride[],
  changes: AttentionChange[],
  written: Pick<ServerOverride, 'basis' | 'set_by' | 'set_at'>,
): ServerOverride[] {
  let out = [...overrides];
  for (const c of changes) {
    out = out.filter((o) => !sameLogin(o.login, c.login));
    if (c.state) out.push({ login: loginKey(c.login), state: c.state, ...written });
  }
  return out;
}

/** A member's reason as short display text, e.g. "changes requested by bo". */
export function reasonText(r: Reason): string {
  switch (r.kind) {
    case 'reviewRequested':
      return 'review requested';
    case 'draft':
      return 'draft';
    case 'readyToMerge':
      return 'approved, ready to merge';
    case 'authorReplied':
      return 'author replied';
    case 'newChanges':
      return 'new changes since your review';
    case 'noReviewers':
      return 'no reviewers requested';
    case 'waiting':
      return 'waiting on the author';
    case 'manual':
      return 'added manually';
    case 'changesRequestedBy':
      return `changes requested by ${r.login}`;
    case 'unresolvedFrom':
      return `unresolved comments from ${r.login}`;
    case 'newCommentsFrom':
      return `new comments from ${r.login}`;
    case 'newCommitsFrom':
      return `new commits from ${r.login}`;
  }
}

/**
 * The attention set as it will look once `changes` apply, shown before the
 * server confirms: added people join, or stay in place, with the engine's
 * manual reason; removed ones leave. Clearing an override leaves the member as is until
 * recomputed.
 */
export function applyToMembers(members: AttentionMember[], changes: AttentionChange[]): AttentionMember[] {
  let out = [...members];
  for (const c of changes) {
    if (c.state === 'remove') out = out.filter((m) => !sameLogin(m.login, c.login));
    else if (c.state === 'add') {
      const at = out.findIndex((m) => sameLogin(m.login, c.login));
      if (at < 0) out.push({ login: c.login, reason: { kind: 'manual' } });
      else out[at] = { ...out[at], reason: { kind: 'manual' } };
    }
  }
  return out;
}

export interface Participant {
  login: string;
  /** Roles on this PR, most significant first: author, reviewer, requested, commented. */
  roles: string[];
}

/**
 * People who can be put in the attention set: the author, reviewers,
 * requested reviewers (users; teams are not expanded) and everyone who
 * commented (threads and the conversation). Bots are left out.
 */
export function participantsOf(pr: PullRequestData, threads: ReviewThread[], timeline: TimelineItem[]): Participant[] {
  const roles = new Map<string, Participant>();
  const add = (login: string | null | undefined, role: string) => {
    if (!login || login.endsWith('[bot]')) return;
    const key = loginKey(login);
    let p = roles.get(key);
    if (!p) roles.set(key, (p = { login, roles: [] }));
    if (!p.roles.includes(role)) p.roles.push(role);
  };
  if (pr.author && !isBot(pr.author)) add(pr.author.login, 'author');
  for (const r of pr.latestReviews.nodes) if (r.author && !isBot(r.author)) add(r.author.login, 'reviewer');
  for (const n of pr.reviewRequests.nodes) add(reviewerLogin(n.requestedReviewer), 'requested');
  for (const t of threads)
    for (const c of t.comments.nodes)
      if (c.author && !isBot(c.author) && c.pullRequestReview?.state !== 'PENDING') add(c.author.login, 'commented');
  for (const it of timeline)
    if (it.__typename === 'IssueComment' && it.author && !isBot(it.author)) add(it.author.login, 'commented');
  return [...roles.values()];
}

/** Participants whose login contains `q` (case-insensitive), in their order. */
export function filterParticipants(list: Participant[], q: string): Participant[] {
  const needle = q.trim().replace(/^@/, '').toLowerCase();
  return needle ? list.filter((p) => p.login.toLowerCase().includes(needle)) : list;
}
